import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback } from "react";

import { parseIndexBody, searchOpsApi } from "@/lib/api/search";

export const Route = createFileRoute("/_authenticated/search/indexes")({
  component: SearchIndexesPage,
});

function SearchIndexesPage() {
  const q = useQuery({
    queryKey: ["search", "indexes"],
    queryFn: () => searchOpsApi.getIndexes(),
  });

  const refresh = useCallback(() => {
    void q.refetch();
  }, [q]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Indexes</h2>
          <p className="text-muted-foreground text-sm">
            OpenSearch index inventory with generations and endpoints.
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-3 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          disabled={q.isFetching}
        >
          {q.isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>
      {q.isError && (
        <div className="bg-destructive/10 text-destructive border-destructive/30 rounded-md border px-3 py-2 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {q.data && q.data.count === 0 && (
        <div className="bg-card text-card-foreground rounded-lg border p-10 text-center">
          <p className="text-muted-foreground">No indexes found.</p>
        </div>
      )}
      {q.data?.indexes.map((row) => {
        const index = parseIndexBody(row);
        if (!index) {
          const rowLabel = row.indexId ?? row.id ?? "unknown-index";
          return (
            <div
              key={rowLabel}
              className="bg-card text-card-foreground rounded-lg border border-red-300 p-4"
            >
              <p className="font-medium text-red-700">{rowLabel}</p>
              <p className="text-sm text-red-600">
                Failed to parse index payload
                {row.statusCode ? ` (status ${row.statusCode})` : ""}.
              </p>
            </div>
          );
        }
        return (
          <div
            key={index.id}
            className="bg-card text-card-foreground rounded-lg border p-4"
          >
            <div className="mb-4 flex items-center justify-between gap-3">
              <h3 className="text-lg font-semibold">{index.id}</h3>
              <span className="bg-muted rounded-full px-2 py-1 text-xs">
                {index.version}
              </span>
            </div>
            <p className="text-muted-foreground mb-3 text-sm">
              {index.description}
            </p>
            <p className="mb-1 text-xs">Domain: {index.domain}</p>
            <p className="mb-3 text-xs">Resource: {index.resource}</p>
            <div className="mb-3 flex flex-wrap gap-2">
              {index.languages.map((lang) => (
                <span
                  key={lang}
                  className="bg-muted rounded-full px-2 py-1 text-xs"
                >
                  {lang}
                </span>
              ))}
            </div>
            <div className="space-y-2">
              {index.generations.map((gen) => (
                <div key={gen.id} className="bg-muted rounded-md p-3 text-xs">
                  <p className="font-medium">
                    Generation {gen.id} {gen.active ? "(Active)" : "(Inactive)"}
                  </p>
                  <p>Cluster: {gen.clusterId}</p>
                  <p>Read traffic: {gen.readTraffic}%</p>
                  <p className="break-all">Endpoint: {gen.endpoint}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
